import * as React from "react"
import { forwardRef, MutableRefObject, RefCallback, useLayoutEffect, useRef, useState } from "react"
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  FORCE_AUTOCOMPLETE_OFF_ATTRS,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { X } from "lucide-react"

interface MultiTextInputProps {
  id: string
  label?: string
  error?: boolean
  value?: string[]
  onChange?: (value: string[]) => void
  onInputChange?: (value: string) => void
  placeholder?: string
  options?: { label: string; value: string }[]
  disabled?: boolean
  allowCustom?: boolean
  size?: "default" | "sm"
  formatInput?: (value: string) => string
}

export const MultiTextInput = forwardRef<HTMLInputElement, MultiTextInputProps>(
  (
    {
      label,
      error,
      id,
      value = [],
      onChange,
      onInputChange,
      placeholder,
      options = [],
      disabled,
      allowCustom = true,
      size = "default",
      formatInput,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const triggerRef = useRef<HTMLDivElement>(null)
    const [inputValue, setInputValue] = useState("")
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [contentWidth, setContentWidth] = useState<number | undefined>(undefined)
    const [isWidthApplied, setIsWidthApplied] = useState(false)

    const mergeRefs = (
      ...refs: Array<RefCallback<HTMLInputElement> | MutableRefObject<HTMLInputElement | null> | null>
    ) => {
      return (instance: HTMLInputElement | null) => {
        refs.forEach((ref) => {
          if (typeof ref === "function") {
            ref(instance)
          } else if (ref != null) {
            ref.current = instance
          }
        })
      }
    }

    const allOptionsSelected = options.length > 0 && options.every((opt) => value.includes(opt.label))
    const shouldHideInput = !allowCustom && allOptionsSelected

    const handleAddValue = (newValue: string) => {
      if (!newValue.trim()) return
      const isFromOptions = options.some((opt) => opt.label === newValue.trim())
      if (isFromOptions || allowCustom) {
        if (!value.includes(newValue.trim())) {
          onChange?.([...value, newValue.trim()])
        }
      }
      setInputValue("")
      setIsPopoverOpen(false)
    }

    const handleRemoveValue = (valueToRemove: string) => {
      onChange?.(value.filter((v) => v !== valueToRemove))
      inputRef.current?.focus()
    }

    const handleSelect = (selectedValue: string) => {
      const option = options.find((opt) => opt.value === selectedValue || opt.label === selectedValue)
      if (option) {
        handleAddValue(option.label)
      } else {
        handleAddValue(selectedValue)
      }
      setTimeout(() => inputRef.current?.focus(), 0)
    }

    const handleContainerClick = () => {
      inputRef.current?.focus()
    }

    const filteredOptions = options
      .filter((opt) => !value.includes(opt.label))
      .filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()))

    const showCustomOption =
      allowCustom &&
      inputValue.trim() &&
      !value.includes(inputValue.trim()) &&
      !options.some((opt) => opt.label.toLowerCase() === inputValue.trim().toLowerCase())

    const hasSuggestions = filteredOptions.length > 0 || showCustomOption
    const openState = !!((isPopoverOpen || isFocused) && !shouldHideInput)

    useLayoutEffect(() => {
      if (openState && triggerRef.current) {
        setContentWidth(triggerRef.current.offsetWidth)
        requestAnimationFrame(() => {
          setIsWidthApplied(true)
        })
      } else {
        setContentWidth(undefined)
        setIsWidthApplied(false)
      }
    }, [openState])

    return (
      <div className="space-y-2">
        {label && (
          <Label htmlFor={id} className={cn(error && "text-destructive")}>
            {label}
          </Label>
        )}
        <Popover open={openState} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <div
              ref={triggerRef}
              className={cn(
                "border-input flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border px-3 py-1.5 shadow-xs",
                size === "sm" && "min-h-8 px-2 py-1",
                error && "border-destructive",
                disabled && "cursor-not-allowed opacity-50"
              )}
              onClick={handleContainerClick}
              role="combobox"
              aria-expanded={openState}
              aria-controls="tag-suggestions"
            >
              {value.map((item, index) => (
                <div
                  key={`${item}-${index}`}
                  className="bg-secondary flex h-6 items-center gap-1 rounded-md pr-1 pl-1.5"
                >
                  <span className="text-sm">{item}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveValue(item)
                    }}
                    className="text-foreground hover:text-foreground/80 focus:ring-ring rounded-full p-0.5 focus:ring-1 focus:outline-hidden disabled:opacity-50"
                    disabled={disabled}
                    aria-label={`Remove ${item}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              {!shouldHideInput && (
                <input
                  ref={mergeRefs(ref, inputRef)}
                  id={id}
                  value={inputValue}
                  onChange={(e) => {
                    const raw = e.target.value
                    const formatted = formatInput ? formatInput(raw) : raw
                    setInputValue(formatted)
                    onInputChange?.(formatted)
                    if (raw.trim() || filteredOptions.length > 0) {
                      setIsPopoverOpen(true)
                    }
                  }}
                  onFocus={() => {
                    setIsPopoverOpen(true)
                    setIsFocused(true)
                  }}
                  onBlur={() => {
                    setIsFocused(false)
                  }}
                  placeholder={value.length === 0 ? placeholder : "Add more..."}
                  disabled={disabled}
                  className={cn(
                    "h-6 flex-1 border-0 bg-transparent p-0 focus:ring-0 focus:outline-hidden",
                    size === "sm" && "text-sm"
                  )}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      if (showCustomOption && inputValue.trim()) {
                        handleSelect(inputValue.trim())
                      } else {
                        handleAddValue(inputValue)
                      }
                    }
                    if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
                      e.preventDefault()
                      handleRemoveValue(value[value.length - 1])
                    }
                    if (e.key === "Escape") {
                      setIsPopoverOpen(false)
                    }
                  }}
                  autoCorrect="off"
                  spellCheck="false"
                  {...FORCE_AUTOCOMPLETE_OFF_ATTRS}
                />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            className={cn("p-0 transition-opacity duration-100 ease-out", !isWidthApplied && "opacity-0")}
            style={{ width: contentWidth ? `${contentWidth}px` : undefined, zIndex: 60 }}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if ((e.target as Element)?.closest("[data-radix-popover-trigger]")) {
                e.preventDefault()
                return
              }
              setIsPopoverOpen(false)
            }}
            id="tag-suggestions"
          >
            <Command shouldFilter={false}>
              <CommandList>
                {filteredOptions.length > 0 && (
                  <CommandGroup heading="Suggestions">
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={handleSelect}
                        className="cursor-pointer"
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {showCustomOption && (
                  <CommandGroup heading="Add Custom">
                    <CommandItem value={inputValue.trim()} onSelect={handleSelect} className="cursor-pointer">
                      Add &quot;{inputValue.trim()}&quot;
                    </CommandItem>
                  </CommandGroup>
                )}
                {!hasSuggestions && inputValue && (
                  <div className="text-muted-foreground p-2 text-center text-sm">No results found.</div>
                )}
                {!hasSuggestions && !inputValue && allowCustom && (
                  <div className="text-muted-foreground p-2 text-center text-sm">Type to add new value</div>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)

MultiTextInput.displayName = "MultiTextInput"
